import type { FilterSchema } from '@/shared/components/filter/validation'
import type { BIImpactResponse, FilterRuleModel } from '@/shared/generated'
import type { WidgetPeriods } from '../types'

import { last, pipe, prop } from 'lodash/fp'
import { useTranslation } from 'react-i18next'

import { useBiImpactQuery, zipSeries } from '@/entities/bi'
import { useSelectedCurrency } from '@/entities/currency'

import { Operators } from '@/shared/components/query-builder/constants'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { formatMoney, NumberFormats } from '@/shared/tools/locale'

import { Widget, WidgetContent, WidgetHeader } from '../../Widget'
import WidgetEmptyState from '../../WidgetEmptyState'
import WidgetErrorState from '../../WidgetErrorState'

const METRIC = 'gross_profit'
const BI_IMPACT_FILTER: FilterRuleModel = {
	name: 'acceptance_status',
	operation: Operators.IN,
	value: ['accepted', 'changed_manually'],
}

export default function NumericGrossProfitBoostWidget({
	commonFilters,
	commonPeriods,
}: {
	commonPeriods: WidgetPeriods
	commonFilters: FilterSchema[]
}) {
	const { t } = useTranslation()

	const currency = useSelectedCurrency()
	const {
		data: grossProfitImpact,
		isLoading,
		isError,
	} = useBiImpactQuery(
		{
			currency,
			label: 'NumericGrossProfitBoost',
			start_date: commonPeriods?.main?.start_date,
			end_date: commonPeriods?.main?.end_date,
			metrics: [METRIC],
			filters: [BI_IMPACT_FILTER, ...commonFilters],
		},
		{
			enabled: !!commonPeriods?.main,
			select: (data: BIImpactResponse | null) =>
				pipe(
					(data) => zipSeries(data?.meta, data?.data),
					last,
					prop(METRIC)
				)(data),
		}
	)

	return (
		<Widget className='relative truncate'>
			<WidgetHeader
				title={t('widget.gross_profit_boost.title', {
					defaultValue: 'Gross Profit Boost',
				})}
				tooltip={t('widget.gross_profit_boost.tooltip', {
					defaultValue: 'widget.gross_profit_boost.tooltip',
				})}
				subtitle={t('widget.gross_profit_boost.description', {
					defaultValue: 'widget.gross_profit_boost.description',
				})}
			/>
			<WidgetErrorState isTinted={isError} size='small' />
			<WidgetContent className='mt-auto mb-2 truncate'>
				{isLoading && (
					<div className='space-y-1'>
						<Skeleton className='col-span-2 h-6 w-24' />
						<Skeleton className='col-span-2 h-8 w-32' />
					</div>
				)}
				{!!grossProfitImpact && !isLoading && (
					<p className='truncate text-3xl font-semibold'>
						{formatMoney(grossProfitImpact, currency, NumberFormats.Compact)}
					</p>
				)}
				{!grossProfitImpact && !isLoading && !isError && (
					<WidgetEmptyState size='small' />
				)}
			</WidgetContent>
		</Widget>
	)
}
